import { AdminPageCabecalhoComponent } from '../../shared/admin-page-cabecalho/admin-page-cabecalho.component';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import {
  GruposApiService,
  ListRetrieveGrupo,
  ListRetrieveUsuario,
  Permissao,
  PermissoesApiService,
  PutUsuario,
  PutUsuarioWithRelations,
  UsuariosApiService,
} from '@cei-ufg/ngx-cei-seguranca';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { RouterModule, ActivatedRoute } from '@angular/router';
import { CeiButtonComponent, CeiCheckboxComponent, CeiInputTextComponent, CeiPanelComponent, CeiSnackBarService } from '@cei-ufg/ngx-arquitetura-cei-frontend';
import { notifyAdminFormError, notifyAdminFormSuccess, stopIfAdminFormInvalid } from '../../shared/admin-form.utils';

type Mutable<T> = {
  -readonly [K in keyof T]: T[K];
};

type UsuarioEditFormValue = Mutable<ListRetrieveUsuario>;

type UsuarioUpdatePayload = UsuarioEditFormValue & Pick<PutUsuario, 'password'>;

type UsuarioUpdateWithRelationsPayload = PutUsuarioWithRelations;

@Component({
  selector: 'cei-admin-usuario-edit',
  standalone: true,
  imports: [AdminPageCabecalhoComponent, CommonModule, FormsModule, RouterModule, CeiInputTextComponent, CeiCheckboxComponent, CeiButtonComponent, CeiPanelComponent],
  templateUrl: './edit.component.html',
  styleUrls: ['./edit.component.scss']
})
export class AdminUsuarioEditComponent {
  form: UsuarioEditFormValue = {
    id: 0,
    login: '',
    email: '',
    is_active: true,
    is_staff: false,
    date_joined: '',
    pessoa_id: null,
    user_permissions: [],
    groups: [],
  };
  availablePermissions: Permissao[] = [];
  availableGroups: ListRetrieveGrupo[] = [];
  password = '';
  usuariosApi = inject(UsuariosApiService);
  permissoesApi = inject(PermissoesApiService);
  gruposApi = inject(GruposApiService);
  route = inject(ActivatedRoute);
  cdr = inject(ChangeDetectorRef);
  private readonly snackBar = inject(CeiSnackBarService);

  get registroId(): number {
    return Number(this.route.snapshot.paramMap.get('id'));
  }

  ngOnInit(): void {
    this.loadAvailablePermissions();
    this.loadAvailableGroups();

    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (id) {
      this.usuariosApi.usuarioRetrieve({ id }).subscribe(data => {
        this.form = this.mapUsuarioToForm(data);
        this.password = '';
        this.cdr.markForCheck();
      });
    }
  }

  onSubmit(form: NgForm) {
    if (stopIfAdminFormInvalid(form)) {
      this.cdr.markForCheck();
      return;
    }

    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (id) {
      const updateData: UsuarioUpdatePayload = {
        ...this.form,
        password: this.password,
        user_permissions: this.form.user_permissions,
        groups: this.form.groups,
      };

      if (!this.password?.trim()) {
        delete (updateData as Partial<UsuarioUpdatePayload>).password;
      }

      const hasRelationsSelected = this.form.user_permissions.length > 0 || this.form.groups.length > 0;

      if (hasRelationsSelected) {
        const updateWithRelationsData: UsuarioUpdateWithRelationsPayload = {
          id: this.form.id,
          login: this.form.login,
          email: this.form.email,
          password: this.password,
          date_joined: this.form.date_joined,
          is_active: this.form.is_active,
          is_staff: this.form.is_staff,
          user_permissions: this.form.user_permissions.map((permission) => Number(permission.id)),
          groups: this.form.groups.map((group) => group.id),
        };

        if (!this.password?.trim()) {
          delete (updateWithRelationsData as Partial<UsuarioUpdateWithRelationsPayload>).password;
        }

        this.usuariosApi.usuarioComRelacoesUpdate({ id, putUsuarioWithRelations: updateWithRelationsData }).subscribe({
          next: () => {
            notifyAdminFormSuccess(this.snackBar, 'Usuário atualizado com sucesso!');
            this.cdr.markForCheck();
          },
          error: (err) => {
            notifyAdminFormError(this.snackBar, 'Erro ao atualizar usuário.', err);
            this.cdr.markForCheck();
          }
        });
        return;
      }

      this.usuariosApi.usuarioUpdate({ id, putUsuario: updateData }).subscribe({
        next: () => {
          notifyAdminFormSuccess(this.snackBar, 'Usuário atualizado com sucesso!');
          this.cdr.markForCheck();
        },
        error: (err) => {
          notifyAdminFormError(this.snackBar, 'Erro ao atualizar usuário.', err);
          this.cdr.markForCheck();
        }
      });
    }
  }

  hasPermission(permissionId: string): boolean {
    return this.form.user_permissions.some((permission) => permission.id === permissionId);
  }

  hasGroup(groupId: number): boolean {
    return this.form.groups.some((group) => group.id === groupId);
  }

  togglePermission(permission: Permissao, checked: boolean): void {
    const currentPermissions = [...this.form.user_permissions];
    const nextPermissions = checked
      ? currentPermissions.some((currentPermission) => currentPermission.id === permission.id)
        ? currentPermissions
        : [...currentPermissions, permission]
      : currentPermissions.filter((currentPermission) => currentPermission.id !== permission.id);

    this.form = {
      ...this.form,
      user_permissions: nextPermissions,
    };
  }

  toggleGroup(group: ListRetrieveGrupo, checked: boolean): void {
    const currentGroups = [...this.form.groups];
    const nextGroups = checked
      ? currentGroups.some((currentGroup) => currentGroup.id === group.id)
        ? currentGroups
        : [...currentGroups, group]
      : currentGroups.filter((currentGroup) => currentGroup.id !== group.id);

    this.form = {
      ...this.form,
      groups: nextGroups,
    };
  }

  private loadAvailablePermissions(): void {
    this.permissoesApi.permissaoList({}).subscribe({
      next: (data) => {
        this.availablePermissions = data.results;
        this.cdr.markForCheck();
      },
      error: () => {
        this.availablePermissions = [];
        this.cdr.markForCheck();
      },
    });
  }

  private loadAvailableGroups(): void {
    this.gruposApi.grupoList({}).subscribe({
      next: (data) => {
        this.availableGroups = data.results;
        this.cdr.markForCheck();
      },
      error: () => {
        this.availableGroups = [];
        this.cdr.markForCheck();
      },
    });
  }

  private mapUsuarioToForm(usuario: ListRetrieveUsuario): UsuarioEditFormValue {
    return {
      login: usuario.login,
      email: usuario.email,
      is_active: usuario.is_active ?? true,
      is_staff: usuario.is_staff ?? false,
      id: usuario.id,
      date_joined: usuario.date_joined,
      pessoa_id: usuario.pessoa_id ?? null,
      user_permissions: [...usuario.user_permissions],
      groups: [...usuario.groups],
    };
  }
}
